import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { LcsApiService } from '@lcs/shared/api';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { PromptTypes } from 'generated/graphql-types';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, map, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { extensionsRoute, promptsRoute } from 'src/app/app-routing.module';
import { CurrentUser } from 'src/app/authentication/models/current-user.interface';
import { getCurrentUser } from 'src/app/authentication/state/authentication.selector';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import {
   promptCallHungUp,
   promptReady,
} from 'src/app/signalr/recording-new-prompt-hub/recording-new-prompt-hub.actions';
import { RecordingNewPromptHub } from 'src/app/signalr/recording-new-prompt-hub/recording-new-prompt-hub.service';
import { getSelectedAccountId } from 'src/app/store/select-account/select-account.selector';
import { v4 as uuidv4 } from 'uuid';
import { FileOption } from '../../prompt-details/models/file.option.interface';
import { PromptDetailSaveModel } from '../../prompt-details/models/prompt-detail-save.model';
import { GetPromptDetail, GetPromptDetailVariables } from '../../prompt-details/queries/generated/GetPromptDetail';
import { getPromptDetail } from '../../prompt-details/queries/prompt-detail.graphql';
import { PromptDetailApiService } from '../../prompt-details/services/prompt-detail-api.service';
import * as PromptDetailActions from '../../prompt-details/state/prompt-detail.actions';
import { PromptOption } from '../../prompt-register/models/prompt-option.interface';
import { promptRegisterAdapterActions } from '../../prompt-register/state/prompt-register.actions';
import { RecordedAudioOutput } from '../models/recorded-prompt';
import { FileSaveModel } from '../models/save-file-only-models';
import { AddPromptApiService } from '../services/add-prompt-api.service';
import { AudioRecordingService } from '../services/audio-recording.service';
import * as AddPromptActions from './add-prompt.actions';
import {
   getIsContinuousAdd,
   getIsRecordingFileOnlyState,
   getIsRecordingFromExtensionState,
   getIsRecordingNewFromAdd,
   getRecordingPromptCallDetailId,
   getRecordingPromptCallServerIp,
   getRecordingPromptCallSessionId,
   getRecordingPromptChannelId,
   getVoicemailGreetingPromptState,
} from './add-prompt.selectors';

@Injectable()
export class AddPromptEffects {
   createPrompt$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(AddPromptActions.createPrompt),
         withLatestFrom(this.store.select(getSelectedAccountId)),
         switchMap(([action, accountId]) => {
            return this.createPrompt(action.prompt, accountId, action.file).pipe(
               map((executionResult) => {
                  const promptOption: PromptOption = {
                     file: executionResult.data.prompt.file,
                     isReusable: executionResult.data.prompt.isReusable,
                     name: executionResult.data.prompt.name,
                     promptId: executionResult.data.prompt.promptId,
                     typeId: executionResult.data.prompt.typeId,
                  };
                  const promptId = executionResult.data.prompt.promptId;
                  const promptName = executionResult.data.prompt.name;
                  if (action.routeToNewPromptAfterSave !== false) {
                     return AddPromptActions.createPromptSuccess({ promptId, promptName });
                  } else {
                     return AddPromptActions.createPromptSuccessNoRouteToDetail({ promptItem: promptOption });
                  }
               }),
               catchError((error) => {
                  return of(AddPromptActions.createPromptFailure(error));
               })
            );
         })
      );
   });

   createPromptAndAddAnother$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(AddPromptActions.createPromptAndAddAnother),
         withLatestFrom(this.store.select(getSelectedAccountId)),
         switchMap(([action, accountId]) => {
            return this.createPrompt(action.prompt, accountId, action.file).pipe(
               map((executionResult) => {
                  const promptOption: PromptOption = {
                     file: executionResult.data.prompt.file,
                     isReusable: executionResult.data.prompt.isReusable,
                     name: executionResult.data.prompt.name,
                     promptId: executionResult.data.prompt.promptId,
                     typeId: executionResult.data.prompt.typeId,
                  };
                  const promptId = executionResult.data.prompt.promptId;
                  const promptName = executionResult.data.prompt.name;
                  return AddPromptActions.createPromptAndAddAnotherSuccess({ promptId, promptName });
               }),
               catchError((error) => {
                  return of(AddPromptActions.createPromptFailure(error));
               })
            );
         })
      );
   });

   createFileForUpdatePrompt$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(AddPromptActions.saveFileOnly),
         withLatestFrom(this.store.select(getSelectedAccountId)),
         switchMap(([action]) => {
            return this.createFileForPromptUpdate(action.fileModelToSave).pipe(
               map((executionResult) => {
                  const fileOption: FileOption = {
                     fileId: executionResult.data.file.fileId,
                     fileName: executionResult.data.file.fileName,
                     fileToken: executionResult.data.file.fileToken,
                  };
                  return AddPromptActions.createFileSuccess({ fileItem: fileOption });
               }),
               catchError((error) => {
                  return of(AddPromptActions.createPromptFailure(error));
               })
            );
         })
      );
   });

   recordPrompt$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(AddPromptActions.recordPrompt),
         withLatestFrom(
            this.store.pipe(select(getSelectedAccountId)),
            this.store.pipe(select(getCurrentUser)),
            this.store.pipe(select(getRecordingPromptCallSessionId))
         ),
         switchMap(([action, accountId, currentUser, currentRecordingPromptCallSessionId]) => {
            if (currentRecordingPromptCallSessionId) {
               this.recordingNewPromptHub.unregisterRecordingNewPrompt(currentRecordingPromptCallSessionId);
            }
            const promptRecordingCallSessionId = uuidv4().replace(/-/g, '');
            this.recordingNewPromptHub.registerAsRecordingNewPrompt(promptRecordingCallSessionId);

            return this.recordPrompt(
               action.prompt.prompt,
               accountId,
               currentUser,
               action.routeToExtension,
               action.destinationNumber,
               promptRecordingCallSessionId
            ).pipe(
               map((response) => {
                  return AddPromptActions.recordPromptRequestSend({
                     promptRecordingCallDetailId: response.PromptRecordingCallDetailId,
                     promptRecordingCallSessionId: promptRecordingCallSessionId,
                  });
               }),
               catchError((error) => {
                  return of(AddPromptActions.createPromptFailure(error));
               }),
               takeUntil(this.actions$.pipe(ofType(AddPromptActions.recordPromptCancelPressed)))
            );
         })
      );
   });

   recordNewFileForExistingPrompt$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(AddPromptActions.recordPromptFileOnly),
         withLatestFrom(
            this.store.pipe(select(getSelectedAccountId)),
            this.store.pipe(select(getCurrentUser)),
            this.store.pipe(select(getRecordingPromptCallSessionId))
         ),
         switchMap(([action, accountId, currentUser, currentRecordingPromptCallSessionId]) => {
            if (currentRecordingPromptCallSessionId) {
               this.recordingNewPromptHub.unregisterRecordingNewPrompt(currentRecordingPromptCallSessionId);
            }
            const promptRecordingCallSessionId = uuidv4().replace(/-/g, '');
            this.recordingNewPromptHub.registerAsRecordingNewPrompt(promptRecordingCallSessionId);
            return this.recordNewFileForExistingPrompt(
               accountId ?? 0,
               action.promptId,
               action.name,
               action.promptType,
               currentUser,
               action.routeToExtension,
               action.destinationNumber,
               promptRecordingCallSessionId
            ).pipe(
               map((response) => {
                  return AddPromptActions.recordPromptRequestSend({
                     promptRecordingCallDetailId: response.PromptRecordingCallDetailId,
                     promptRecordingCallSessionId: promptRecordingCallSessionId,
                  });
               }),
               catchError((error) => {
                  return of(AddPromptActions.createPromptFailure(error));
               }),
               takeUntil(this.actions$.pipe(ofType(AddPromptActions.recordPromptCancelPressed)))
            );
         })
      );
   });

   recordPromptFromMicrophone$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(AddPromptActions.recordPromptFromMicrophone),
         switchMap(() => {
            return this.audioRecordingService.getRecordedBlob().pipe(
               map((result) => {
                  const file: RecordedAudioOutput | null = result;
                  const data = {
                     file: file,
                  };
                  return AddPromptActions.stopMicrophoneRecording(data);
               })
            );
         })
      );
   });

   createNewFromExtensionPrompt$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(AddPromptActions.createNewFromExtensionPrompt),
         withLatestFrom(this.store.select(getSelectedAccountId)),
         switchMap(([action, accountId]) => {
            return this.createPrompt(action.prompt, accountId, action.file).pipe(
               map((executionResult) => {
                  return AddPromptActions.createNewFromExtensionPromptSuccess({
                     prompt: executionResult.data,
                     voiceMailGreetingPrompt: action.voiceMailGreetingPrompt,
                  });
               }),
               catchError((error) => {
                  return of(AddPromptActions.createPromptFailure(error));
               })
            );
         })
      );
   });

   createPromptSuccess$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(AddPromptActions.createPromptSuccess),
            tap((result) => {
               this.dialogService.closeFormComponent();
               this.router.navigate([`/${promptsRoute}/${result.promptId}`]);
               this.snackbar.open(`Prompt ${result.promptName} Created`);
            })
         );
      },
      {
         dispatch: false,
      }
   );

   createPromptAndAddAnotherSuccess$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(AddPromptActions.createPromptAndAddAnotherSuccess),
            tap((result) => {
               this.snackbar.open('Prompt Created');
               this.store.dispatch(promptRegisterAdapterActions.pageEnter());
            })
         );
      },
      {
         dispatch: false,
      }
   );

   createPromptSuccessNoRoute$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(AddPromptActions.createPromptSuccessNoRouteToDetail),
            tap((result) => {
               this.snackbar.open(`Prompt ${result.promptItem.name} Created`);
            })
         );
      },
      {
         dispatch: false,
      }
   );

   createFileSuccessNoRoute$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(AddPromptActions.createFileSuccess),
            tap(() => {
               this.snackbar.open(`File Saved. Prompt must be saved to use new file. Cancel to discard file.`);
            })
         );
      },
      {
         dispatch: false,
      }
   );

   promptNotification$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(promptReady),
         withLatestFrom(
            this.store.select(getIsRecordingNewFromAdd),
            this.store.select(getIsRecordingFromExtensionState),
            this.store.select(getVoicemailGreetingPromptState),
            this.store.select(getIsRecordingFileOnlyState),
            this.store.select(getIsContinuousAdd)
         ),
         switchMap(
            ([
               result,
               recordingNewFromAdd,
               recordingNewFromExtension,
               voicemailType,
               recordingFileForUpdatePrompt,
               isContinuous,
            ]) => {
               if (recordingNewFromExtension) {
                  const id = result.promptId;
                  return this.apiService
                     .query<GetPromptDetail, GetPromptDetailVariables>({
                        query: getPromptDetail,
                        variables: { id },
                     })
                     .pipe(
                        map((promptResult) => {
                           this.dialogService.closeFormComponent();
                           return AddPromptActions.recordNewFromExtensionPromptSuccess({
                              data: promptResult.data.prompt,
                              voiceMailGreetingPrompt: voicemailType,
                           });
                        })
                     );
               } else if (recordingNewFromAdd && !recordingFileForUpdatePrompt) {
                  const id = result.promptId;
                  const currentUrl = this.router.url;
                  const isExtensionNumberRoute = currentUrl.includes(extensionsRoute);

                  if (!isContinuous) {
                     if (isExtensionNumberRoute) {
                        return this.apiService
                           .query<GetPromptDetail, GetPromptDetailVariables>({
                              query: getPromptDetail,
                              variables: { id },
                           })
                           .pipe(
                              map((promptResult) => {
                                 this.dialogService.closeFormComponent();

                                 const prompt = promptResult.data.prompt;

                                 return AddPromptActions.createPromptSuccessNoRouteToDetail({
                                    promptItem: {
                                       promptId: prompt.promptId,
                                       name: prompt.name,
                                       file: prompt.file,
                                       isReusable: prompt.isReusable,
                                       typeId: prompt.typeId,
                                    },
                                 });
                              })
                           );
                     } else {
                        this.dialogService.closeFormComponent();
                        this.router.navigate([`/${promptsRoute}/${id}`]);
                        this.snackbar.open('Prompt Created');
                        return EMPTY;
                     }
                  } else {
                     return of(
                        AddPromptActions.createPromptAndAddAnotherSuccess({
                           promptId: id,
                           promptName: '',
                        })
                     );
                  }
               } else if (recordingFileForUpdatePrompt) {
                  const id = result.promptId;
                  return this.apiService
                     .query<GetPromptDetail, GetPromptDetailVariables>({
                        query: getPromptDetail,
                        variables: { id },
                     })
                     .pipe(
                        map((promptResult) => {
                           this.dialogService.closeFormComponent();
                           return AddPromptActions.createFileFromCallToRecordSuccess({
                              prompt: promptResult.data.prompt,
                           });
                        })
                     );
               } else {
                  const id = result.promptId;
                  return this.apiService
                     .query<GetPromptDetail, GetPromptDetailVariables>({
                        query: getPromptDetail,
                        variables: { id },
                     })
                     .pipe(
                        map((promptResult) => {
                           return PromptDetailActions.savePromptDetailSuccess({
                              data: {
                                 prompt: {
                                    accountId: promptResult.data.prompt.promptId,
                                    dontDeleteFileOnUpdate: false,
                                    copyMohFile: null,
                                    file: promptResult.data.prompt.file,
                                    fileId: promptResult.data.prompt.fileId,
                                    isActive: promptResult.data.prompt.isActive,
                                    isReusable: promptResult.data.prompt.isReusable,
                                    name: promptResult.data.prompt.name,
                                    promptId: promptResult.data.prompt.promptId,
                                    typeId: promptResult.data.prompt.typeId,
                                 },
                              },
                           });
                        })
                     );
               }
            }
         )
      );
   });

   promptCallHangUpCancelClicked$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(AddPromptActions.recordPromptCancelPressed),
         withLatestFrom(
            this.store.select(getRecordingPromptCallServerIp),
            this.store.select(getRecordingPromptChannelId),
            this.store.select(getRecordingPromptCallDetailId)
         ),
         switchMap(([result, serverIp, channelId, callDetailId]) => {
            return of(this.addPromptApiService.hangupActiveCall(serverIp, channelId, callDetailId)).pipe(
               map(() => {
                  return promptCallHungUp();
               })
            );
         })
      );
   });

   constructor(
      private actions$: Actions,
      private addPromptApiService: AddPromptApiService,
      private promptDetailApiService: PromptDetailApiService,
      private audioRecordingService: AudioRecordingService,
      private apiService: LcsApiService,
      private store: Store,
      private snackbar: MatSnackBar,
      private dialogService: LcsDialogService,
      private router: Router,
      private recordingNewPromptHub: RecordingNewPromptHub
   ) {}

   private createPrompt(prompt: PromptDetailSaveModel, accountId: number | null, file: File | null) {
      const promptToSave: PromptDetailSaveModel = {
         ...prompt,
         accountId: accountId ?? 0,
         file: {
            ...prompt.file,
            accountId: accountId ?? 0,
            fields: prompt?.file?.fields ?? [],
         },
         isActive: true,
         promptId: prompt.promptId ?? null,
      };
      return this.addPromptApiService.create(promptToSave, file, prompt.promptId);
   }

   private createFileForPromptUpdate(fileModel: FileSaveModel) {
      return this.addPromptApiService.createFileOnly(fileModel);
   }

   private recordPrompt(
      prompt: PromptDetailSaveModel,
      accountId: number | null,
      currentUser: CurrentUser | null,
      routeToExtension: string,
      destinationNumber: string,
      sessionId: string
   ): Observable<{ PromptRecordingCallDetailId: number }> {
      const promptToSave: PromptDetailSaveModel = {
         ...prompt,
         accountId: accountId ?? 0,
         isActive: true,
      };

      return this.addPromptApiService.callExtensionToRecordPrompt(
         promptToSave,
         currentUser,
         routeToExtension,
         destinationNumber,
         sessionId
      );
   }

   private recordNewFileForExistingPrompt(
      accountId: number,
      promptId: number,
      name: string,
      promptType: PromptTypes,
      currentUser: CurrentUser | null,
      routeToExtension: string,
      destinationNumber: string,
      sessionId: string
   ): Observable<{ PromptRecordingCallDetailId: number }> {
      return this.addPromptApiService.callToUpdateExistingPromptFile(
         accountId,
         promptId,
         name,
         promptType,
         currentUser,
         routeToExtension,
         destinationNumber,
         sessionId
      );
   }
}
